import moment from 'moment-timezone/moment-timezone-utils';
import {DurationTuple} from '@shared/api/model/timestamp';

export interface DLSInfo {
  shiftPeriod: DurationTuple;
  dlsBound: moment.Moment;
}

interface TimezoneBoundInfo {
  untils: moment.Moment[];
  offsets: number[];
}

export class TimezoneUtil {
  private static MINUTES_PER_HOUR = 60;
  private static cache = new Map<string, Map<number, TimezoneBoundInfo>>();

  private static getFromCache(timezoneName: string, year: number): TimezoneBoundInfo {
    return TimezoneUtil.cache.get(timezoneName)?.get(year) ?? null;
  }

  private static storeInCache(timezoneName: string, year: number, data: TimezoneBoundInfo): void {
    let dataByYear = TimezoneUtil.cache.get(timezoneName);
    if (dataByYear == null) {
      dataByYear = new Map();
      TimezoneUtil.cache.set(timezoneName, dataByYear);
    }
    dataByYear.set(year, data);
  }


  private static getDLSInfoForYear(timezoneName: string, year: number): TimezoneBoundInfo {
    const data = this.getFromCache(timezoneName, year);
    if (data != null) {
      return data;
    }

    const zone = moment.tz.zone(timezoneName);
    if (zone) {
      const tzData = moment.tz.filterYears(zone,  year);
      if (tzData) {
        const data: TimezoneBoundInfo = {
          untils: (tzData.untils || []).map(ts => ts != null ? moment.tz(ts, timezoneName) : null),
          offsets: tzData.offsets != null ? [...tzData.offsets] : []
        };
        this.storeInCache(timezoneName, year, data);
        return data;
      }
    }

    return null;
  }

  private static convertToDuration(minutes: number): DurationTuple {
    if (minutes === 0) {
      throw new Error('Should not be 0');
    }
    const remainder = minutes % this.MINUTES_PER_HOUR;
    return remainder === 0 ? {amount: minutes / this.MINUTES_PER_HOUR, unit: 'hour'} : {amount: minutes, unit: 'minute'};
  }

  public static getDLSInfo(ts: moment.Moment, granularity?: moment.unitOfTime.StartOf): DLSInfo {
    const dlsData = TimezoneUtil.getDLSInfoForYear(ts.tz(), ts.year());
    if (dlsData) {
      // untils will have a null value at the end
      const index = (dlsData?.untils || []).findIndex(dlsTs => dlsTs?.isSame(ts, granularity));
      if (index > -1) {
        if (index < dlsData.offsets.length - 1) {
          let offsetDiff: number = dlsData.offsets[index + 1] - dlsData.offsets[index];
          if (offsetDiff !== 0) {
            // The offset is stored inverted as specified by IANA
            return {shiftPeriod: this.convertToDuration(-offsetDiff), dlsBound: dlsData.untils[index]};
          }
        } else {
          // Weird, should not happen! The last entry in the 'untils' array should be null, and hence the only index
          // that should be found is 1 before the last index
        }
      }
    }

    return null;
  }
}
